<template>
  <div class="footerPageLayout wd-bg-white sm:(wd-bg-transparent)">
    <div v-if="isMobile" class="background--white" />
    <div v-else class="tab-bg-white" />

    <h1 v-if="isMobile" class="wd-py-6 wd-px-5 wd-text-otempmax wd-font-medium">{{ mobileTitle }}</h1>
    <v-tabs v-else v-model="currentTab" height="64" bg-color="white" color="orange50">
      <client-only>
        <v-tab
          v-for="tab in tabs"
          :key="tab.title"
          class="pageTab !wd-py-5 !wd-px-4"
          :exact="tab.isExactPath"
          :to="tab.localePath"
          :value="tab.value"
        >
          <h1 class="wd-text-omd wd-font-normal">{{ tab.title }}</h1>
        </v-tab>
      </client-only>
    </v-tabs>

    <div v-if="isMobile" ref="drawerOpener" class="openerRow wd-flex wd-py-4 wd-px-5" @click="handleDrawerShow">
      <funnow-icon class="wd-mr-2" max-width="24" height="24" image-path="list.svg" />
      <p class="wd-text-omd !wd-mb-0">{{ pageData.list[selectedListItemIndex].text }}</p>
    </div>

    <template v-if="isMobile">
      <v-navigation-drawer
        v-model="isNavigationDrawerShow"
        class="navigationDrawer wd-z-9 wd-fixed"
        width="600"
        :style="{ top: `${drawerTop}px`, height: `${drawerHeight}px` }"
        elevation="0"
        :scrim="false"
      >
        <v-list class="wd-overscroll-y-contain">
          <v-list-item
            v-for="(item, index) in pageData.list"
            :key="item.text"
            :tag="isInTermsPage ? 'a' : 'div'"
            class="!wd-py-4 !wd-px-5 wd-text-omd"
            :class="selectedListItemIndex === index ? '!wd-text-orange50' : '!wd-text-black50'"
            style="min-height: unset"
            @click="handleListItemClick(item, index)"
          >
            {{ item.text }}
          </v-list-item>
        </v-list>
      </v-navigation-drawer>

      <div class="mobileContent">
        <slot />
      </div>
    </template>
    <div v-else class="wd-flex wd-pt-6 wd-mb-7">
      <!-- width is from (100% - 24px * 3) / 4 = 25% - 18px -->
      <v-list tag="aside" class="wd-mr-6 !wd-rounded-4px" style="width: calc(25% - 18px); height: fit-content">
        <v-list-item
          v-for="(item, index) in pageData.list"
          :key="item.text"
          :tag="isInTermsPage ? 'a' : 'div'"
          class="!wd-py-4 !wd-px-5 !wd-text-omd"
          :class="selectedListItemIndex === index ? '!wd-text-orange50' : '!wd-text-black50'"
          style="min-height: unset"
          @click="handleListItemClick(item, index)"
        >
          {{ item.text }}
        </v-list-item>
      </v-list>

      <div class="desktopContent bg-white wd-px-5 wd-flex-1 wd-rounded-4px" style="height: fit-content">
        <h2 class="wd-pt-10 wd-pb-6 wd-text-otempmax wd-font-medium">
          {{ pageData.list[selectedListItemIndex].text }}
        </h2>

        <slot />
      </div>
    </div>
  </div>
</template>

<script>
import { templateRef, useVModel } from '@vueuse/core'

import { useVuetify } from '@/composition/useUtil'
import FunnowIcon from '@/components/icons/funnow-icon.vue'

export default defineComponent({
  name: 'FooterPageLayout',
  components: {
    FunnowIcon,
  },
  props: {
    selectedListItemIndexFromProps: { type: Number, default: 0 },
    pageData: { type: Object, default: () => ({}) },
  },
  emits: ['itemClick', 'update:selectedListItemIndexFromProps'],
  setup(props, { emit }) {
    const nuxtApp = useNuxtApp()
    const route = useRoute()
    const router = useRouter()
    const vuetify = useVuetify()
    const drawerOpener = templateRef('drawerOpener')
    const selectedListItemIndex = useVModel(props, 'selectedListItemIndexFromProps', emit)

    const innerHeight = ref(0)
    const currentTab = ref(route.path)
    const drawerTop = ref(0)
    const drawerHeight = ref(0)
    const isNavigationDrawerShow = ref(false)

    const isMobile = computed(() => vuetify.display.xs.value)
    const isInTermsPage = computed(() => route.name.includes('terms'))
    const contactLocalePath = computed(() => nuxtApp.$localePath({ name: 'contact' }))

    const mobileTitle = computed(() => {
      const termsLocalePath = nuxtApp.$localePath({ name: 'terms' })

      if (route.path.split?.('?')[0] === contactLocalePath.value.split('?')[0]) {
        return nuxtApp.$i18n.t('footer_contact_us')
      }
      if (route.path.startsWith?.(termsLocalePath)) {
        return nuxtApp.$i18n.t('footer_term')
      }
      return ''
    })
    const tabs = computed(() => [
      {
        title: nuxtApp.$i18n.t('footer_contact_us'),
        isExactPath: true,
        localePath: contactLocalePath.value,
        value: contactLocalePath.value,
      },
      {
        title: nuxtApp.$i18n.t('footer_term'),
        isExactPath: false,
        localePath: nuxtApp.$localePath({
          name: 'terms-consumer',
          query: route.query,
        }),
        value: `/${nuxtApp.$i18n.locale.value}/terms`,
      },
    ])

    watch(
      () => route.path,
      () => {
        currentTab.value = tabs.value.find(tab => route.path.includes(tab.value))?.value || currentTab.value
      }
    )

    watch(isNavigationDrawerShow, isShow => {
      if (isShow) {
        window.document.querySelector('html').style.overflow = 'hidden'
      } else {
        window.document.querySelector('html').style.overflow = 'scroll'
      }
    })

    onMounted(() => {
      currentTab.value = tabs.value.find(tab => route.path.includes(tab.value))?.value || currentTab.value
      innerHeight.value = window.innerHeight
    })

    function handleDrawerShow() {
      const distanceFromViewTop = drawerOpener.value.getBoundingClientRect().top

      drawerTop.value = Math.round(distanceFromViewTop)
      drawerHeight.value = innerHeight.value - drawerTop.value
      isNavigationDrawerShow.value = true
    }

    function handleListItemClick(item, index) {
      if (isInTermsPage.value) {
        router.push(item.localePath)
        return
      }

      isNavigationDrawerShow.value = false
      selectedListItemIndex.value = index
      emit('itemClick', item)
    }

    return {
      isMobile,
      mobileTitle,
      currentTab,
      tabs,
      isNavigationDrawerShow,
      handleDrawerShow,
      drawerTop,
      drawerHeight,
      isInTermsPage,
      handleListItemClick,
      selectedListItemIndex,
    }
  },
  head() {
    const pageTitle = `${this.pageData.title} | ${this.$t('funnow_title')}`
    return {
      title: pageTitle,
      meta: [
        { hid: 'title', name: 'title', content: pageTitle },
        { hid: 'og:title', property: 'og:title', content: pageTitle },
      ],
    }
  },
})
</script>

<style lang="scss" scoped>
.tab-bg-white {
  position: absolute;
  width: 100%;
  height: 64px;
  top: 0;
  left: 0;
  z-index: -1;
  background-color: white;
}

.footerPageLayout {
  .pageTab.v-tab {
    font-size: $baseFontSize;
    font-weight: 400;
    text-transform: none;
    letter-spacing: 0;
    &:not(.v-tab--selected) {
      /* to override a tag color */
      color: $black50 !important;
    }
  }
  :deep(.v-tabs-bar) {
    height: auto;
  }
}

.navigationDrawer {
  :deep(.v-navigation-drawer__border) {
    width: 0;
  }
}

@include xxs {
  .openerRow {
    border-bottom: 1px solid $gray40;
  }
}
</style>
